/**
 * Character drawing primitives for the pod-room scene.
 *
 * Each agent gets a circular body (already drawn by the scene) plus:
 *   * a tinted, role-specific accessory icon hovering above the head;
 *   * a face -- two eyes + a mouth -- whose shape is driven by the
 *     latest scene_hint.mood from the backend.
 *
 * Everything is rendered with Pixi ``Graphics`` primitives so the
 * characters scale with the viewport, ship inside the existing pixi
 * chunk (no asset loading), and the suite can mock them out by
 * stubbing this module.  References:
 *
 *   * ``PodPlatform/docs/DASHBOARD_EVENT_SEQUENCES.md`` -- which
 *     ``actor`` emits which event, with the canonical mood mapping;
 *   * ``PodPlatform/docs/dashboard_events.schema.json`` -- the closed
 *     ``EventActor`` enum.
 */

import type { Graphics } from 'pixi.js';

/**
 * Mood-driven face: two eye dots + a mouth curve.  Drawn at the
 * origin of the passed Graphics; the caller is responsible for
 * positioning / scaling.  ``radius`` is the head radius -- features
 * lay out relative to it so the same code works for the 26-px mobile
 * agent and the 88-px desktop PM.
 */
/**
 * Optional layer-2 controls on top of the mood-driven base face.
 *
 * - ``blinking``: replaces both eyes with a thin horizontal line for
 *   the duration of one blink (~120 ms in the caller).  Cheaper than
 *   a separate animation system -- the caller decides timing.
 * - ``eyeShiftX`` / ``eyeShiftY``: shift the pupils within the eye
 *   socket for "saccade" effect (eyes look left / right / down).
 *   Values are in head-radius units, typical range ±0.15.
 */
export interface FaceOptions {
  blinking?: boolean;
  eyeShiftX?: number;
  eyeShiftY?: number;
}

export function drawFace(
  g: Graphics,
  radius: number,
  mood: string,
  color = 0x0d1117,
  options: FaceOptions = {},
): void {
  const eyeR = Math.max(1.6, radius * 0.09);
  const eyeOffsetX = radius * 0.32;
  const eyeOffsetY = -radius * 0.12;
  const mouthY = radius * 0.28;
  const mouthW = radius * 0.55;
  const shiftX = (options.eyeShiftX ?? 0) * radius;
  const shiftY = (options.eyeShiftY ?? 0) * radius;

  // Blink: a single horizontal line for each eye, drawn instead of
  // any mood-specific eye shape.  Mood-specific brows (angry) and
  // mouth still render below.  Skipped for ``frozen``/``sad`` so
  // the "X" / drooping eyes stay readable.
  if (options.blinking && mood !== 'frozen' && mood !== 'sad') {
    g.moveTo(-eyeOffsetX - eyeR, eyeOffsetY)
      .lineTo(-eyeOffsetX + eyeR, eyeOffsetY)
      .moveTo(eyeOffsetX - eyeR, eyeOffsetY)
      .lineTo(eyeOffsetX + eyeR, eyeOffsetY)
      .stroke({ width: 1.6, color, alpha: 0.95 });
    if (mood === 'angry') {
      g.moveTo(-eyeOffsetX - eyeR * 1.4, eyeOffsetY - eyeR * 2)
        .lineTo(-eyeOffsetX + eyeR * 1.4, eyeOffsetY - eyeR * 0.5)
        .stroke({ width: 1.8, color, alpha: 0.95 });
      g.moveTo(eyeOffsetX + eyeR * 1.4, eyeOffsetY - eyeR * 2)
        .lineTo(eyeOffsetX - eyeR * 1.4, eyeOffsetY - eyeR * 0.5)
        .stroke({ width: 1.8, color, alpha: 0.95 });
    }
    drawMouth(g, mood, mouthY, mouthW, radius, eyeR, color);
    return;
  }

  // Eyes
  if (mood === 'frozen') {
    // Tightly closed eyes -- read as "shivering / hibernating", not
    // "dead".  Two short horizontal lines, slightly thicker than the
    // ambient blink so it stays distinguishable from a regular blink.
    g.moveTo(-eyeOffsetX - eyeR * 1.05, eyeOffsetY)
      .lineTo(-eyeOffsetX + eyeR * 1.05, eyeOffsetY)
      .moveTo(eyeOffsetX - eyeR * 1.05, eyeOffsetY)
      .lineTo(eyeOffsetX + eyeR * 1.05, eyeOffsetY)
      .stroke({ width: 2.0, color, alpha: 0.95 });
  } else if (mood === 'sad') {
    // Sad: small dots, lowered.
    g.circle(-eyeOffsetX, eyeOffsetY + eyeR, eyeR).fill({ color });
    g.circle(eyeOffsetX, eyeOffsetY + eyeR, eyeR).fill({ color });
  } else if (mood === 'thinking') {
    // Half-closed eyes (horizontal lines).
    g.moveTo(-eyeOffsetX - eyeR, eyeOffsetY)
      .lineTo(-eyeOffsetX + eyeR, eyeOffsetY)
      .moveTo(eyeOffsetX - eyeR, eyeOffsetY)
      .lineTo(eyeOffsetX + eyeR, eyeOffsetY)
      .stroke({ width: 1.6, color, alpha: 0.9 });
  } else if (mood === 'celebrating' || mood === 'excited') {
    // Star-shaped sparkle eyes.
    drawSparkle(g, -eyeOffsetX, eyeOffsetY, eyeR * 1.8, color);
    drawSparkle(g, eyeOffsetX, eyeOffsetY, eyeR * 1.8, color);
  } else if (mood === 'confident' || mood === 'proud') {
    // Happy closed eyes (small upward arcs).  Pixi 8 keeps the path
    // open across ``stroke()`` calls -- without an explicit moveTo
    // the second arc would draw an implicit connecting line from the
    // first arc's endpoint, leaving a visible stroke across the face.
    g.moveTo(-eyeOffsetX - eyeR * 1.2, eyeOffsetY + eyeR * 0.4)
      .arc(-eyeOffsetX, eyeOffsetY + eyeR * 0.4, eyeR * 1.2, Math.PI, 2 * Math.PI)
      .stroke({ width: 1.6, color, alpha: 0.95 });
    g.moveTo(eyeOffsetX - eyeR * 1.2, eyeOffsetY + eyeR * 0.4)
      .arc(eyeOffsetX, eyeOffsetY + eyeR * 0.4, eyeR * 1.2, Math.PI, 2 * Math.PI)
      .stroke({ width: 1.6, color, alpha: 0.95 });
  } else if (mood === 'angry') {
    // Angled eyebrows over normal eyes -- "knit brow".
    g.circle(-eyeOffsetX + shiftX, eyeOffsetY + shiftY, eyeR).fill({ color });
    g.circle(eyeOffsetX + shiftX, eyeOffsetY + shiftY, eyeR).fill({ color });
    g.moveTo(-eyeOffsetX - eyeR * 1.4, eyeOffsetY - eyeR * 2)
      .lineTo(-eyeOffsetX + eyeR * 1.4, eyeOffsetY - eyeR * 0.5)
      .stroke({ width: 1.8, color, alpha: 0.95 });
    g.moveTo(eyeOffsetX + eyeR * 1.4, eyeOffsetY - eyeR * 2)
      .lineTo(eyeOffsetX - eyeR * 1.4, eyeOffsetY - eyeR * 0.5)
      .stroke({ width: 1.8, color, alpha: 0.95 });
  } else {
    // Default: simple dot eyes -- shift pupils for saccades.
    g.circle(-eyeOffsetX + shiftX, eyeOffsetY + shiftY, eyeR).fill({ color });
    g.circle(eyeOffsetX + shiftX, eyeOffsetY + shiftY, eyeR).fill({ color });
  }

  drawMouth(g, mood, mouthY, mouthW, radius, eyeR, color);
}

function drawMouth(
  g: Graphics,
  mood: string,
  mouthY: number,
  mouthW: number,
  radius: number,
  eyeR: number,
  color: number,
): void {
  if (mood === 'thinking') {
    const dx = radius * 0.18;
    g.circle(-dx, mouthY, eyeR * 0.7).fill({ color, alpha: 0.7 });
    g.circle(0, mouthY, eyeR * 0.7).fill({ color, alpha: 0.7 });
    g.circle(dx, mouthY, eyeR * 0.7).fill({ color, alpha: 0.7 });
  } else if (mood === 'confident' || mood === 'celebrating' || mood === 'excited' || mood === 'proud') {
    // moveTo to arc start breaks Pixi 8's implicit lineTo from the
    // previous path position -- otherwise the smile gets a tail
    // drawn from wherever the eye-shape left off.
    g.moveTo(mouthW * 0.5, mouthY - radius * 0.05)
      .arc(0, mouthY - radius * 0.05, mouthW * 0.5, 0, Math.PI)
      .stroke({ width: 1.8, color, alpha: 0.95 });
  } else if (mood === 'worried' || mood === 'sad' || mood === 'angry') {
    g.moveTo(-mouthW * 0.5, mouthY + radius * 0.18)
      .arc(0, mouthY + radius * 0.18, mouthW * 0.5, Math.PI, 2 * Math.PI)
      .stroke({ width: 1.8, color, alpha: 0.95 });
  } else if (mood === 'frozen') {
    // Pursed lips -- short straight line, slightly shorter than the
    // idle line so the whole face reads as "clenched against the
    // cold" rather than "neutral".
    g.moveTo(-mouthW * 0.22, mouthY)
      .lineTo(mouthW * 0.22, mouthY)
      .stroke({ width: 1.8, color, alpha: 0.95 });
  } else {
    g.moveTo(-mouthW * 0.32, mouthY)
      .lineTo(mouthW * 0.32, mouthY)
      .stroke({ width: 1.6, color, alpha: 0.8 });
  }
}

/**
 * Role-specific accessory icon, drawn at ``(cx, cy)`` with the given
 * ``size`` (icon's bounding-box side length).  ``accent`` tints fills
 * + strokes; pass the agent's mood colour so the icon matches the
 * ring.
 */
export function drawRoleIcon(
  g: Graphics,
  role: string,
  cx: number,
  cy: number,
  size: number,
  accent: number,
): void {
  const s = size;
  switch (role) {
    case 'qa': // Quant -- mini bar chart
      drawBars(g, cx, cy, s, accent);
      return;
    case 'sa': // Sentiment -- speech bubble
      drawSpeechBubble(g, cx, cy, s, accent);
      return;
    case 'fa': // Fundamental -- balance scale / clipboard
      drawClipboard(g, cx, cy, s, accent);
      return;
    case 'researcher': // magnifier
      drawMagnifier(g, cx, cy, s, accent);
      return;
    case 'rm': // risk manager -- shield
      drawShield(g, cx, cy, s, accent);
      return;
    case 'ea': // execution -- upward arrow
      drawArrow(g, cx, cy, s, accent);
      return;
    case 'pm': // portfolio manager -- crown
      drawCrown(g, cx, cy, s, accent);
      return;
    case 'risk_officer': // exclamation triangle
      drawWarningTriangle(g, cx, cy, s, accent);
      return;
    case 'policy_updater': // gear
      drawGear(g, cx, cy, s, accent);
      return;
    case 'leaderboard_keeper': // trophy
      drawTrophy(g, cx, cy, s, accent);
      return;
    default:
      // Generic dot.
      g.circle(cx, cy, s * 0.3).fill({ color: accent });
  }
}

// ===== icon primitives =================================================

function drawBars(g: Graphics, cx: number, cy: number, size: number, accent: number): void {
  const w = size * 0.18;
  const xs = [-1, 0, 1].map((i) => cx + i * w * 1.4);
  const heights = [0.45, 0.85, 0.65];
  for (let i = 0; i < 3; i++) {
    const h = (heights[i] ?? 0.6) * size;
    g.rect((xs[i] ?? 0) - w / 2, cy + size * 0.4 - h, w, h)
      .fill({ color: accent });
  }
}

function drawSpeechBubble(
  g: Graphics,
  cx: number,
  cy: number,
  size: number,
  accent: number,
): void {
  const r = size * 0.42;
  g.roundRect(cx - r, cy - r * 0.75, r * 2, r * 1.3, r * 0.35).fill({
    color: accent,
    alpha: 0.9,
  });
  // Little tail.
  g.moveTo(cx - r * 0.4, cy + r * 0.55)
    .lineTo(cx - r * 0.7, cy + r * 0.95)
    .lineTo(cx - r * 0.05, cy + r * 0.55)
    .fill({ color: accent, alpha: 0.9 });
}

function drawClipboard(
  g: Graphics,
  cx: number,
  cy: number,
  size: number,
  accent: number,
): void {
  const w = size * 0.65;
  const h = size * 0.85;
  g.roundRect(cx - w / 2, cy - h / 2, w, h, 3)
    .fill({ color: accent, alpha: 0.9 });
  // Clip at top.
  g.roundRect(cx - w * 0.22, cy - h / 2 - 3, w * 0.45, 5, 1).fill({
    color: 0x0d1117,
  });
  // Two text lines.
  g.rect(cx - w * 0.3, cy - h * 0.1, w * 0.6, 1.5).fill({ color: 0x0d1117 });
  g.rect(cx - w * 0.3, cy + h * 0.1, w * 0.4, 1.5).fill({ color: 0x0d1117 });
}

function drawMagnifier(
  g: Graphics,
  cx: number,
  cy: number,
  size: number,
  accent: number,
): void {
  const r = size * 0.32;
  // Lens.
  g.circle(cx - size * 0.08, cy - size * 0.08, r)
    .stroke({ width: 2.4, color: accent });
  // Handle.
  g.moveTo(cx + r * 0.45, cy + r * 0.45)
    .lineTo(cx + r * 1.1, cy + r * 1.1)
    .stroke({ width: 2.4, color: accent });
}

function drawShield(
  g: Graphics,
  cx: number,
  cy: number,
  size: number,
  accent: number,
): void {
  const w = size * 0.6;
  const h = size * 0.9;
  g.moveTo(cx, cy - h / 2)
    .lineTo(cx + w / 2, cy - h / 2 + h * 0.2)
    .lineTo(cx + w / 2, cy + h * 0.1)
    .quadraticCurveTo(cx + w / 2, cy + h / 2, cx, cy + h / 2)
    .quadraticCurveTo(cx - w / 2, cy + h / 2, cx - w / 2, cy + h * 0.1)
    .lineTo(cx - w / 2, cy - h / 2 + h * 0.2)
    .closePath()
    .fill({ color: accent, alpha: 0.9 });
  // Inner cross.
  g.rect(cx - 1, cy - h * 0.18, 2, h * 0.4).fill({ color: 0x0d1117 });
  g.rect(cx - h * 0.14, cy - 1, h * 0.28, 2).fill({ color: 0x0d1117 });
}

function drawArrow(
  g: Graphics,
  cx: number,
  cy: number,
  size: number,
  accent: number,
): void {
  const h = size * 0.9;
  const w = size * 0.45;
  g.moveTo(cx, cy - h / 2)
    .lineTo(cx + w / 2, cy)
    .lineTo(cx + w * 0.18, cy)
    .lineTo(cx + w * 0.18, cy + h / 2)
    .lineTo(cx - w * 0.18, cy + h / 2)
    .lineTo(cx - w * 0.18, cy)
    .lineTo(cx - w / 2, cy)
    .closePath()
    .fill({ color: accent });
}

function drawCrown(
  g: Graphics,
  cx: number,
  cy: number,
  size: number,
  accent: number,
): void {
  const w = size * 0.95;
  const h = size * 0.55;
  // Three points with valleys.
  g.moveTo(cx - w / 2, cy + h / 2)
    .lineTo(cx - w / 2, cy - h * 0.1)
    .lineTo(cx - w * 0.28, cy + h * 0.15)
    .lineTo(cx - w * 0.1, cy - h / 2)
    .lineTo(cx + w * 0.1, cy + h * 0.15)
    .lineTo(cx + w * 0.28, cy - h / 2)
    .lineTo(cx + w * 0.45, cy + h * 0.15)
    .lineTo(cx + w / 2, cy - h * 0.1)
    .lineTo(cx + w / 2, cy + h / 2)
    .closePath()
    .fill({ color: accent });
  // Three gemstones.
  g.circle(cx - w * 0.3, cy - h * 0.18, 1.6).fill({ color: 0xffffff });
  g.circle(cx, cy - h * 0.32, 2).fill({ color: 0xffffff });
  g.circle(cx + w * 0.3, cy - h * 0.18, 1.6).fill({ color: 0xffffff });
}

function drawWarningTriangle(
  g: Graphics,
  cx: number,
  cy: number,
  size: number,
  accent: number,
): void {
  const s = size * 0.9;
  g.moveTo(cx, cy - s / 2)
    .lineTo(cx + s / 2, cy + s / 2)
    .lineTo(cx - s / 2, cy + s / 2)
    .closePath()
    .fill({ color: accent });
  // Exclamation mark inside.
  g.rect(cx - 1.2, cy - s * 0.12, 2.4, s * 0.32).fill({ color: 0x0d1117 });
  g.circle(cx, cy + s * 0.3, 1.6).fill({ color: 0x0d1117 });
}

function drawGear(
  g: Graphics,
  cx: number,
  cy: number,
  size: number,
  accent: number,
): void {
  const r = size * 0.34;
  const teeth = 8;
  const toothLen = r * 0.4;
  // Teeth as small rectangles around the rim.
  for (let i = 0; i < teeth; i++) {
    const a = (i / teeth) * 2 * Math.PI;
    const tx = cx + Math.cos(a) * (r + toothLen / 2);
    const ty = cy + Math.sin(a) * (r + toothLen / 2);
    g.rect(tx - 2, ty - toothLen / 2, 4, toothLen)
      .fill({ color: accent });
  }
  g.circle(cx, cy, r).fill({ color: accent });
  g.circle(cx, cy, r * 0.45).fill({ color: 0x0d1117 });
}

function drawTrophy(
  g: Graphics,
  cx: number,
  cy: number,
  size: number,
  accent: number,
): void {
  const w = size * 0.55;
  const h = size * 0.6;
  // Cup.
  g.moveTo(cx - w / 2, cy - h / 2)
    .lineTo(cx + w / 2, cy - h / 2)
    .lineTo(cx + w * 0.35, cy + h * 0.15)
    .lineTo(cx - w * 0.35, cy + h * 0.15)
    .closePath()
    .fill({ color: accent });
  // Stem.
  g.rect(cx - w * 0.08, cy + h * 0.15, w * 0.16, h * 0.18)
    .fill({ color: accent });
  // Base.
  g.rect(cx - w * 0.35, cy + h * 0.33, w * 0.7, h * 0.12)
    .fill({ color: accent });
  // Handles.  Explicit moveTo before each arc to break Pixi 8's
  // implicit path continuation (otherwise the second handle's start
  // gets a connector from the first handle's end).
  g.moveTo(cx - w / 2, cy - h * 0.15 - w * 0.18)
    .arc(cx - w / 2, cy - h * 0.15, w * 0.18, -Math.PI / 2, Math.PI / 2)
    .stroke({ width: 2, color: accent });
  g.moveTo(cx + w / 2, cy - h * 0.15 + w * 0.18)
    .arc(cx + w / 2, cy - h * 0.15, w * 0.18, Math.PI / 2, -Math.PI / 2)
    .stroke({ width: 2, color: accent });
}

function drawSparkle(
  g: Graphics,
  cx: number,
  cy: number,
  size: number,
  color: number,
): void {
  const r = size * 0.5;
  // 4-point star.
  g.moveTo(cx, cy - r)
    .lineTo(cx + r * 0.3, cy - r * 0.3)
    .lineTo(cx + r, cy)
    .lineTo(cx + r * 0.3, cy + r * 0.3)
    .lineTo(cx, cy + r)
    .lineTo(cx - r * 0.3, cy + r * 0.3)
    .lineTo(cx - r, cy)
    .lineTo(cx - r * 0.3, cy - r * 0.3)
    .closePath()
    .fill({ color });
}
